/**
 * A read receipt — what a fetch actually covered.
 *
 * This is a fact about a network read, so it lives in `io/`. `selectors/` turns
 * it into a scope (`selectors/population.ts`); `domain/` never sees it.
 *
 * ## Why `coversWholeBook` is a separate field from `since === null`
 *
 * The obvious encoding is "unbounded ⇔ whole book", and it is wrong. An
 * adversarial pass caught it: the production path to a whole book is
 * **bounded boot + backfill**, which has a non-null `since` forever. Encoding
 * whole-book as `since === null` makes the only real path to a Class-L number
 * unreachable — so Class L would be blocked permanently rather than for the few
 * seconds the backfill takes, and the first person to hit that would "fix" it by
 * forging `since: null`, defeating the guard entirely.
 *
 * So coverage is tracked explicitly, and `mergeReceipts` is the ONLY way to earn
 * it from two partial reads.
 */

export interface ReadReceipt {
  readonly table: 'trades';
  /** The `date >=` bound applied, or null for an unbounded read. */
  readonly since: string | null;
  readonly pages: number;
  /**
   * The paging loop ended on a short/empty page with no error.
   *
   * ⚠ NOT the same as "no error". A read that stopped because it hit a page cap,
   * or because the caller only asked for one page, is un-terminated: rows exist
   * that were never fetched, and any aggregate over the result is short.
   */
  readonly terminated: boolean;
  /** Every row for this user is present. Only `mergeReceipts` or an unbounded read earns this. */
  readonly coversWholeBook: boolean;
}

/** An unbounded read that ran to exhaustion. */
export function wholeBookReceipt(pages: number): ReadReceipt {
  return { table: 'trades', since: null, pages, terminated: true, coversWholeBook: true };
}

/** A bounded read. `terminated` says whether it exhausted the rows INSIDE the bound. */
export function boundedReceipt(since: string, pages: number, terminated: boolean): ReadReceipt {
  return { table: 'trades', since, pages, terminated, coversWholeBook: false };
}

/** A read that failed or was cut short. Can never become a whole book. */
export function partialReceipt(since: string | null, pages: number): ReadReceipt {
  return { table: 'trades', since, pages, terminated: false, coversWholeBook: false };
}

/**
 * Combine a bounded boot read with the backfill that completed it.
 *
 * The ONLY way a bounded read becomes a whole book. Both halves must have
 * terminated, and the backfill must be the complement of the boot bound — i.e.
 * everything strictly before `boot.since`. A backfill that stopped early, or one
 * bounded to a different date, does not earn whole-book coverage.
 */
export function mergeReceipts(boot: ReadReceipt, backfill: ReadReceipt): ReadReceipt {
  const complete =
    boot.terminated &&
    backfill.terminated &&
    // The backfill must reach all the way back — an unbounded tail read.
    backfill.since === null;
  return {
    table: 'trades',
    since: boot.since,
    pages: boot.pages + backfill.pages,
    terminated: boot.terminated && backfill.terminated,
    coversWholeBook: complete,
  };
}
